import { Dispatch, AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import {
  CREATE_USUARIO_REQUEST,
  CREATE_USUARIO_SUCCESS,
  CREATE_USUARIO_FAILURE,
  FETCH_USUARIOS_REQUEST,
  FETCH_USUARIOS_SUCCESS,
  FETCH_USUARIOS_FAILURE,
  FETCH_USUARIO_REQUEST,
  FETCH_USUARIO_SUCCESS,
  FETCH_USUARIO_FAILURE,
  UPDATE_USUARIO_REQUEST,
  UPDATE_USUARIO_SUCCESS,
  UPDATE_USUARIO_FAILURE,
  DELETE_USUARIO_REQUEST,
  DELETE_USUARIO_SUCCESS,
  DELETE_USUARIO_FAILURE,
  SET_USUARIOS_PAGE,
  SET_USUARIOS_LIMIT
} from '../../../constants/usuarios/usuarios';
import { message } from 'antd';
import { RootState } from '../../store/store';
import fetchWithIP from '../utils/FechHeaders';

export interface UsuarioDto {
  nombre: string;
  apellidos?: string;
  tipoUsuarioId?: number;
  empresaId: number;
  sucursalesIds: number[];
  usuario?: string;
  contrasena: string;
  contrasena_actual?: string; 
  email: string;
  pais?: string;
  telefono?: string;
  imagen_perfil?: string;
  confirmacion?: boolean;
  estado?: boolean;
  timezone?: string;
}

export const createUsuarioRequest = () => ({
  type: CREATE_USUARIO_REQUEST
});

export const createUsuarioSuccess = (data: any) => ({
  type: CREATE_USUARIO_SUCCESS,
  payload: data
});

export const createUsuarioFailure = (error: string) => ({
  type: CREATE_USUARIO_FAILURE,
  payload: error
});

export const fetchUsuariosRequest = () => ({
  type: FETCH_USUARIOS_REQUEST
});

export const fetchUsuariosSuccess = (data: any) => ({
  type: FETCH_USUARIOS_SUCCESS,
  payload: data
});

export const fetchUsuariosFailure = (error: string) => ({
  type: FETCH_USUARIOS_FAILURE,
  payload: error
});

export const fetchUsuarioRequest = () => ({
  type: FETCH_USUARIO_REQUEST
});

export const fetchUsuarioSuccess = (data: any) => ({
  type: FETCH_USUARIO_SUCCESS,
  payload: data
});

export const fetchUsuarioFailure = (error: string) => ({
  type: FETCH_USUARIO_FAILURE,
  payload: error
});

export const updateUsuarioRequest = () => ({
  type: UPDATE_USUARIO_REQUEST
});

export const updateUsuarioSuccess = (data: any) => ({
  type: UPDATE_USUARIO_SUCCESS,
  payload: data
});

export const updateUsuarioFailure = (error: string) => ({
  type: UPDATE_USUARIO_FAILURE,
  payload: error
});

export const deleteUsuarioRequest = () => ({
  type: DELETE_USUARIO_REQUEST
});

export const deleteUsuarioSuccess = (usuarioId: string) => ({
  type: DELETE_USUARIO_SUCCESS,
  payload: usuarioId
});

export const deleteUsuarioFailure = (error: string) => ({
  type: DELETE_USUARIO_FAILURE,
  payload: error
});

export const setUsuariosPage = (page: number) => ({
  type: SET_USUARIOS_PAGE,
  payload: page
});

export const setUsuariosLimit = (limit: number) => ({
  type: SET_USUARIOS_LIMIT,
  payload: limit
});

export const createUsuario = (
  usuarioData: UsuarioDto
): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => {
  return async (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) => {
    dispatch(createUsuarioRequest());

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No se encontró token de autenticación');
      }

      const response = await fetchWithIP(
        'usuarios',
        {
          method: 'POST',
        },
        usuarioData
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al crear el usuario');
      }

      dispatch(createUsuarioSuccess(data));
      message.success('Usuario creado exitosamente');
      return data;
    } catch (error: any) {
      const errorMsg = error.message || 'Error desconocido al crear el usuario';
      dispatch(createUsuarioFailure(errorMsg));
      message.error(errorMsg);
      throw error;
    }
  };
};

export const fetchUsuarios = (
  page: number = 1,
  limit: number = 10,
  empresaId?: number
): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => {
  return async (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) => {
    dispatch(fetchUsuariosRequest());

    try {
      let url = `usuarios?page=${page}&limit=${limit}`;
      if (empresaId) {
        url += `&empresaId=${empresaId}`;
      }

      const response = await fetchWithIP(
        url,
        {
          method: 'GET',
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al obtener los usuarios');
      }

      dispatch(fetchUsuariosSuccess(data));
      return data;
    } catch (error: any) {
      const errorMsg = error.message || 'Error desconocido al obtener los usuarios';
      dispatch(fetchUsuariosFailure(errorMsg));
      message.error(errorMsg);
      throw error;
    }
  };
};

export const updateUsuario = (
  usuarioId: string,
  usuarioData: Partial<UsuarioDto>
): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => {
  return async (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) => {
    dispatch(updateUsuarioRequest());

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No se encontró token de autenticación');
      }

      const dataToSend = Object.fromEntries(
        Object.entries(usuarioData).filter(([_, v]) => v !== undefined && v !== null)
      );

      console.log('Datos a enviar al backend:', dataToSend);

      const response = await fetchWithIP(
        `usuarios/${usuarioId}`,
        {
          method: 'PUT',
        },
        dataToSend
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al actualizar el usuario');
      }

      dispatch(updateUsuarioSuccess(data));
      message.success('Usuario actualizado exitosamente');
      return data;
    } catch (error: any) {
      const errorMsg = error.message || 'Error desconocido al actualizar el usuario';
      dispatch(updateUsuarioFailure(errorMsg));
      message.error(errorMsg);
      throw error;
    }
  };
};

export const deleteUsuario = (
  usuarioId: string
): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => {
  return async (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) => {
    dispatch(deleteUsuarioRequest());

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No se encontró token de autenticación');
      }

      const response = await fetchWithIP(
        `usuarios/${usuarioId}`,
        {
          method: 'DELETE',
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al eliminar el usuario');
      }

      dispatch(deleteUsuarioSuccess(usuarioId));
      message.success('Usuario eliminado exitosamente');
      return data;
    } catch (error: any) {
      const errorMsg = error.message || 'Error desconocido al eliminar el usuario';
      dispatch(deleteUsuarioFailure(errorMsg));
      message.error(errorMsg);
      throw error;
    }
  };
};